/**
 * The queue: what needs a person, worst first.
 *
 * The tiles above the table are not decoration. They answer the question
 * somebody has when they open this at nine in the morning - how much is
 * there, how much of it is broken, and is the reader behaving - before
 * they read a single row.
 *
 * The table is sorted by suspicion and is meant to be abandoned halfway:
 * you work down until the findings stop being worth your time.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";

import { api } from "./api";
import { money, shortDate, timestamp } from "./format";
import type { Analytics, QueuePage, QueueRow, SampleKind } from "./types";
import type { Shell } from "./App";
import { Chip, Icon, StatusChip, Tile } from "./ui";

const STATES = [
  { value: "", label: "All" },
  { value: "needs_review", label: "Needs review" },
  { value: "extracted", label: "Passed" },
  { value: "approved", label: "Approved" },
];

export function QueueView() {
  const [page, setPage] = useState<QueuePage | null>(null);
  const [stats, setStats] = useState<Analytics | null>(null);
  const [status, setStatus] = useState("");
  const [source, setSource] = useState("");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [samples, setSamples] = useState<SampleKind[]>([]);
  const [running, setRunning] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const shell = useOutletContext<Shell>();

  useEffect(() => {
    api.samples().then(setSamples).catch(() => setSamples([]));
  }, []);

  const load = useCallback(async () => {
    try {
      const [rows, summary] = await Promise.all([
        api.queue({
          status: status || undefined,
          source: source || undefined,
          q: query || undefined,
        }),
        api.analytics({ source: source || undefined }),
      ]);
      setPage(rows);
      setStats(summary);
      setError(null);
    } catch (exc) {
      setError(exc instanceof Error ? exc.message : String(exc));
    }
  }, [status, source, query]);

  useEffect(() => {
    const timer = setTimeout(load, query ? 220 : 0);
    return () => clearTimeout(timer);
  }, [load, query]);

  async function upload(files: FileList | null) {
    if (!files?.length) return;
    setBusy(true);
    setError(null);
    try {
      let last = "";
      for (const file of Array.from(files)) {
        last = (await api.upload(file)).id;
      }
      await load();
      shell?.documentsChanged();
      if (files.length === 1 && last) navigate(`/documents/${last}`);
    } catch (exc) {
      setError(exc instanceof Error ? exc.message : String(exc));
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  /**
   * Run one of the ready-made documents.
   *
   * It goes straight to the document afterwards, because the thing worth
   * seeing is the reading beside the page it came from, not another row
   * in a table. The broken sample is the one that matters: it lands in
   * `needs_review`, which is the behaviour a fresh queue never shows.
   */
  async function runSample(kind: string) {
    setRunning(kind);
    setError(null);
    try {
      const detail = await api.runSample(kind);
      await load();
      shell?.documentsChanged();
      navigate(`/documents/${detail.id}`);
    } catch (exc) {
      setError(exc instanceof Error ? exc.message : String(exc));
    } finally {
      setRunning(null);
    }
  }

  const pct = (value: number | null | undefined) =>
    value === null || value === undefined ? "—" : `${(value * 100).toFixed(1)}%`;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Review queue</h1>
          <p>
            Documents are ordered by how much they want a human. Suspicion comes
            from each document's own arithmetic, so it needs no ground truth and
            works the same on an invoice nobody has ever labelled.
          </p>
        </div>
      </div>

      {error && <div className="banner">{Icon.alert({ size: 17 })} {error}</div>}

      <div className="tiles">
        <Tile
          label={source ? `Documents (${source})` : "Documents"}
          value={stats ? stats.documents : "—"}
          foot={
            stats
              ? `${stats.extractions} readings of ${
                  source ? "these" : "them"
                }`
              : ""
          }
        />
        <Tile
          label="Needs review"
          value={stats ? stats.needs_review : "—"}
          tone={stats && stats.needs_review > 0 ? "warn" : undefined}
          foot="errors, or nothing readable"
        />
        <Tile
          label="Unreadable"
          value={stats ? stats.unreadable : "—"}
          tone={stats && stats.unreadable > 0 ? "danger" : undefined}
          foot="no text layer, or refused"
        />
        <Tile
          label="Validation pass"
          value={pct(stats?.validation_pass_rate)}
          tone="accent"
          foot="own numbers agree"
        />
        <Tile
          label="Fields filled"
          value={pct(stats?.field_completeness)}
          foot="of the five that matter"
        />
      </div>

      <div
        className={`dropzone${dragging ? " over" : ""}`}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          void upload(event.dataTransfer.files);
        }}
      >
        <div>
          <strong>{busy ? "Reading the document" : "Drop invoices here"}</strong>
          <p>
            PDF. A digital one is read on this machine, exactly and for free. A
            scan has no text to read, so it goes to a parsing service in the EU
            first, and the reading says which of the two happened.
          </p>
          {busy && (
            <div className="progress">
              <i />
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            ref={fileInput}
            type="file"
            accept="application/pdf"
            multiple
            hidden
            onChange={(event) => void upload(event.target.files)}
          />
          <button
            className="btn primary"
            disabled={busy}
            onClick={() => fileInput.current?.click()}
          >
            {Icon.upload({ size: 15 })} Choose files
          </button>
        </div>
      </div>

      {samples.length > 0 && (
        <div className="samples">
          <div className="samples-lead">
            <strong>No invoice to hand?</strong>
            <span className="note">
              Run one of these. Both are read by the same pipeline as anything
              you upload, and you can delete them afterwards.
            </span>
          </div>
          <div className="samples-actions">
            {samples.map((sample) => (
              <button
                key={sample.key}
                className="btn sample"
                disabled={busy || running !== null}
                onClick={() => void runSample(sample.key)}
              >
                <span className="sample-label">
                  {running === sample.key ? "Reading it" : sample.label}
                </span>
                <span className="sample-blurb">{sample.blurb}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="toolbar">
        <div className="grow" style={{ position: "relative" }}>
          <span
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--muted)",
              display: "flex",
            }}
          >
            {Icon.search({ size: 15 })}
          </span>
          <input
            className="field-control"
            style={{ width: "100%", paddingLeft: 32 }}
            type="search"
            placeholder="Vendor, invoice number or filename"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label="Search the queue"
          />
        </div>

        <div className="segmented" role="group" aria-label="Filter by state">
          {STATES.map((option) => (
            <button
              key={option.value}
              aria-pressed={status === option.value}
              onClick={() => setStatus(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <select
          className="field-control"
          value={source}
          onChange={(event) => setSource(event.target.value)}
          aria-label="Filter by source"
        >
          <option value="">Every source</option>
          <option value="upload">Uploaded</option>
          <option value="sample">Sample</option>
          <option value="synthetic">Synthetic</option>
        </select>

        <span className="note" style={{ marginLeft: "auto" }}>
          {page ? `${page.rows.length} of ${page.total}` : ""}
        </span>
      </div>

      {page && page.rows.length === 0 ? (
        <div className="empty">
          <h2>Nothing matches</h2>
          <p>
            Drop an invoice above, or fill the database with generated ones:
            <br />
            <br />
            <code>docker compose exec app python -m service.seed --count 50</code>
          </p>
        </div>
      ) : (
        <div className="card">
          <div className="table-wrap tall">
            <table className="data">
              <thead>
                <tr>
                  <th style={{ width: 132 }}>State</th>
                  <th>Vendor / file</th>
                  <th>Invoice</th>
                  <th>Issued</th>
                  <th className="right">Total</th>
                  <th>Findings</th>
                  <th>Source</th>
                  <th className="right">Susp.</th>
                </tr>
              </thead>
              <tbody>
                {page?.rows.map((row) => (
                  <Row key={row.extraction_id} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="note" style={{ marginTop: 14 }}>
        An unreadable document scores highest: nothing was extracted, so there is
        nothing to be partially right about. Warnings alone never stop the line -
        holding soft findings in a queue trains people to clear it without reading.
      </p>
    </div>
  );
}

function Row({ row }: { row: QueueRow }) {
  const navigate = useNavigate();
  const href = `/documents/${row.document_id}`;
  const tone = !row.ok ? "danger" : row.error_count ? "danger" : row.warning_count ? "warn" : "ok";

  return (
    <tr
      onClick={(event) => {
        if ((event.target as HTMLElement).closest("a")) return;
        navigate(href);
      }}
    >
      <td>
        <StatusChip status={row.status} ok={row.ok} />
      </td>
      <td>
        <span className="rowlink">
          <span className={`dot ${tone}`} />
          <Link className="strong" to={href}>
            {row.vendor_name ?? row.filename}
          </Link>
        </span>
        <span className="sub">
          {row.vendor_name ? row.filename : timestamp(row.uploaded_at)}
        </span>
      </td>
      <td className="mono">{row.invoice_number ?? <span className="sub">not found</span>}</td>
      <td className="mono">{shortDate(row.issue_date) || "—"}</td>
      <td className="right num strong">{money(row.total, row.currency) || "—"}</td>
      <td>
        <span style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {row.error_count > 0 && (
            <Chip tone="danger">
              {row.error_count} error{row.error_count > 1 ? "s" : ""}
            </Chip>
          )}
          {row.warning_count > 0 && (
            <Chip tone="warn">
              {row.warning_count} warning{row.warning_count > 1 ? "s" : ""}
            </Chip>
          )}
          {row.ok && !row.error_count && !row.warning_count && (
            <span className="note">none</span>
          )}
        </span>
      </td>
      <td>
        <Chip tone={row.source === "synthetic" ? "info" : "neutral"}>{row.source}</Chip>
      </td>
      <td className="right num">{row.suspicion}</td>
    </tr>
  );
}
